/**
 * Reducer for questionActivity state
 * (Note: before the Redux refactor, this state used to be in the viewState for
 * skilldelivery view)
 */

import { fromJS, Map } from 'immutable';
import {
    SET_QUESTION_ACTIVITY,
    REMOVE_QUESTION_ACTIVITY,
    SET_ANSWERS_SUBMITTED,
    SET_NUMERIC_ANSWER,
    SELECT_CARD,
    CLEAR_SELECTED_CARD,
    DRAG_CARD,
    SELECT_CHOICE,
    TOGGLE_CHOICE,
    SET_STYLES,
    RESET_STYLES,
    UPDATE_ACTIVITY_WITH_SEEK_HELP,
    UPDATE_ACTIVITY_WITH_RESULT,
    SET_EXPRESSION_ANSWER,
} from './actions';
import {
    getQuestionActivity,
    setQuestionActivity,
    setNumericAnswerToQuestionActivityInput,
    setExpressionAnswerToQuestionActivityInput,
    selectCardForQuestionActivityStep,
    clearSelectedCardForQuestionActivityStep,
    dragSelectedCardForQuestionActivityStep,
    selectChoiceForQuestionActivityStep,
    toggleChoiceForQuestionActivityStep,
    setStylesForQuestionActivityStep,
    preprocessQuestion,
} from './question-activity';

export const QUESTION_ACTIVITY_KEY = 'questionActivity';

export const QUESTION_STAGES = {
    question: 'question',
    result: 'result',
    seekHelp: 'seek-help',
    wac: 'wac',
};

const initialState = fromJS({
    activities: {},
});

const updateInput = (state, attemptId, step, updater) =>
    state.updateIn(['activities', attemptId, 'info', 'pc', 'steps', step, 'input'], updater);

export function questionActivityReducer(state = initialState, action) {
    let newState;
    let questionActivity;
    let activities = state.get('activities');

    switch (action.type) {
        // Set a question activity in the model:
        case SET_QUESTION_ACTIVITY:
            questionActivity = fromJS({
                info: action.info,
                stage: action.stage,
                taskItemId: action.taskItemId,
                style: {},
            });

            // Run any preprocess actions for the question (e.g. shuffling tiles)
            questionActivity = preprocessQuestion(questionActivity);

            activities = activities.set(action.attemptId, questionActivity);
            newState = state.set('activities', activities);
            return newState;

        // Remove a question activity from the model
        case REMOVE_QUESTION_ACTIVITY:
            activities = activities.delete(action.attemptId);
            return state.set('activities', activities);

        case UPDATE_ACTIVITY_WITH_RESULT:
            questionActivity = getQuestionActivity(state, action.attemptId);

            if (!questionActivity) {
                return state;
            }

            questionActivity = questionActivity.set('stage', QUESTION_STAGES.result);
            questionActivity = questionActivity.set('evaluation', action.evaluation);
            questionActivity = questionActivity.set('message', action.message);
            questionActivity = questionActivity.set('image', action.image);
            questionActivity = questionActivity.set('givenAnswerXML', action.givenAnswerXML);

            activities = activities.set(action.attemptId, questionActivity);
            return state.set('activities', activities);

        case UPDATE_ACTIVITY_WITH_SEEK_HELP:
            questionActivity = getQuestionActivity(state, action.attemptId);

            if (!questionActivity) {
                return state;
            }

            questionActivity = questionActivity.set('stage', QUESTION_STAGES.seekHelp);
            activities = activities.set(action.attemptId, questionActivity);
            return state.set('activities', activities);

        case SET_ANSWERS_SUBMITTED:
            return state.setIn(
                ['activities', action.attemptId, 'submitted'],
                action.answersSubmitted,
            );

        // Set a numeric answer in a question answer:
        case SET_NUMERIC_ANSWER:
            questionActivity = getQuestionActivity(state, action.attemptId);

            if (!questionActivity) {
                return state;
            }

            questionActivity = setNumericAnswerToQuestionActivityInput(
                questionActivity,
                action.stepNumber,
                action.numberFieldId,
                action.answer,
            );

            return setQuestionActivity(state, action.attemptId, questionActivity);

        // Set a expression answer in a question answer:
        case SET_EXPRESSION_ANSWER:
            questionActivity = getQuestionActivity(state, action.attemptId);

            if (!questionActivity) {
                return state;
            }

            questionActivity = setExpressionAnswerToQuestionActivityInput(
                questionActivity,
                action.stepNumber,
                action.numberFieldId,
                action.answer,
            );

            return setQuestionActivity(state, action.attemptId, questionActivity);

        // Select a card in a question answer:
        case SELECT_CARD:
            questionActivity = getQuestionActivity(state, action.attemptId);

            // Ignore if the question activity has been removed (e.g. if
            // user was dragging a card while the view was being popped)
            if (!questionActivity) {
                return state;
            }

            questionActivity = selectCardForQuestionActivityStep(
                questionActivity,
                action.stepNumber,
                action.cardId,
            );
            return setQuestionActivity(state, action.attemptId, questionActivity);

        // Clear the selected card in a question answer:
        case CLEAR_SELECTED_CARD:
            questionActivity = getQuestionActivity(state, action.attemptId);

            // Ignore if the question activity has been removed (e.g. if
            // user was dragging a card while the view was being popped)
            if (!questionActivity) {
                return state;
            }

            questionActivity = clearSelectedCardForQuestionActivityStep(
                questionActivity,
                action.stepNumber,
            );
            return setQuestionActivity(state, action.attemptId, questionActivity);

        // Drag a card in a question answer:
        case DRAG_CARD:
            questionActivity = getQuestionActivity(state, action.attemptId);

            // Ignore if the question activity has been removed (e.g. if
            // user was dragging a card while the view was being popped)
            if (!questionActivity) {
                return state;
            }

            questionActivity = dragSelectedCardForQuestionActivityStep(
                questionActivity,
                action.stepNumber,
                action.slotId,
            );
            return setQuestionActivity(state, action.attemptId, questionActivity);

        // Select an individual choice in a question answer
        case SELECT_CHOICE:
            questionActivity = getQuestionActivity(state, action.attemptId);
            if (!questionActivity) {
                return state;
            }

            questionActivity = selectChoiceForQuestionActivityStep(
                questionActivity,
                action.stepNumber,
                action.choiceId,
                action.selected,
            );
            return setQuestionActivity(state, action.attemptId, questionActivity);

        // Toggle a choice in a question answer:
        case TOGGLE_CHOICE:
            questionActivity = getQuestionActivity(state, action.attemptId);

            if (!questionActivity) {
                return state;
            }

            questionActivity = toggleChoiceForQuestionActivityStep(
                questionActivity,
                action.stepNumber,
                action.choiceId,
            );
            return setQuestionActivity(state, action.attemptId, questionActivity);

        // Set styles for a question activity input (e.g. if the screen-size
        // is changed and the font-size needs to be tweaked)
        case SET_STYLES:
            questionActivity = getQuestionActivity(state, action.attemptId);

            if (!questionActivity) {
                return state;
            }

            questionActivity = setStylesForQuestionActivityStep(
                questionActivity,
                action.stepNumber,
                action.styles,
            );
            return setQuestionActivity(state, action.attemptId, questionActivity);

        case RESET_STYLES:
            return updateInput(
                state,
                action.attemptId,
                action.stepNumber,
                input => input && input.set('styles', Map()),
            );
    }

    return state;
}
